class SearchDrawer extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;

    // The header section wrapper (.section-header) is position:sticky and this
    // snippet renders INSIDE it. For the desktop in-content layout we place the
    // search as a sibling right AFTER it, so it sits in the normal page flow
    // (between header and hero), pushes content down, and scrolls away with the
    // page instead of being glued to the sticky header.
    this._sectionHeader = this.closest('.section-header');

    this.input = this.querySelector('#SearchDrawerInput');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.desktop = window.matchMedia('(min-width: 990px)');

    this.addEventListener('click', (e) => {
      // The dismiss (×) icon — in-field on desktop, the outside "cancel" on mobile.
      if (e.target.closest('.tk-search__dismiss, .tk-search__cancel')) {
        e.preventDefault();
        this.close();
        return;
      }
      // MOBILE ONLY: tapping the overlay (off the field/dropdown) also closes.
      // Desktop is dismissed only via the × / header toggle — no click-off.
      if (!this.desktop.matches && !e.target.closest('.tk-search')) this.close();
    });

    // Clicking empty (non-interactive) space inside the popup must not blur the
    // input, otherwise predictive-search would collapse the results. Real
    // controls (links, buttons, the input) still behave normally.
    this.addEventListener('mousedown', (e) => {
      if (!e.target.closest('.tk-search')) return;
      if (e.target.closest('a, button, input, select, textarea, label')) return;
      e.preventDefault();
    });

    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && this.classList.contains('active')) this.close();
    });
  }

  // Pin the page behind the mobile full-screen drawer so it can't scroll.
  // overflow:hidden alone is ignored by iOS Safari for touch; position:fixed with
  // a negative top is the reliable lock (scroll position restored on unlock).
  lockScroll() {
    // If the page is already pinned by something else (e.g. the open burger menu,
    // over which the search opens on top), leave that lock in place — don't
    // double-lock, and don't take ownership.
    if (
      document.body.classList.contains('menu-drawer-lock') ||
      document.body.classList.contains('search-drawer-lock')
    ) {
      this._lockedByMe = false;
      return;
    }
    this._lockedByMe = true;
    this._scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `${-this._scrollY}px`;
    document.body.classList.add('search-drawer-lock');
  }

  unlockScroll() {
    // Only release the lock if we were the ones who set it — otherwise the
    // (still-open) menu behind the search keeps the page pinned.
    if (!this._lockedByMe) return;
    this._lockedByMe = false;
    if (!document.body.classList.contains('search-drawer-lock')) return;
    document.body.classList.remove('search-drawer-lock');
    document.body.style.top = '';
    window.scrollTo(0, this._scrollY || 0);
  }

  // Scroll the expanding search into view, just below the sticky header.
  scrollIntoPlace() {
    const headerEl = document.querySelector('.header-wrapper');
    const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
    const y = this.getBoundingClientRect().top + window.pageYOffset - headerH - 6;
    if (y < window.pageYOffset) window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }

  open(triggeredBy) {
    this.activeElement = triggeredBy || document.activeElement;
    clearTimeout(this._collapseTimer);
    clearTimeout(this._releaseTimer);

    if (this.desktop.matches) {
      // In-content layout: sit in the page flow right after the (sticky) header.
      if (this._sectionHeader && this.previousElementSibling !== this._sectionHeader) {
        this._sectionHeader.after(this);
      }
      this.style.visibility = '';
      // Surface the recommendations BEFORE animating so the bar and the dropdown
      // are both present and reveal together as the section expands.
      this.predictiveSearch?.showEmptyState?.();
      this.style.maxHeight = '0px';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.add('active');
          // Animate to the measured content height (full, unclipped).
          this.style.maxHeight = this.scrollHeight + 'px';
          this.scrollIntoPlace();
        });
      });
      // After the open transition, release the cap so results can grow/shrink freely.
      this._releaseTimer = setTimeout(() => {
        if (this.classList.contains('active')) this.style.maxHeight = 'none';
      }, 480);
      // Focus once it's expanded (without yanking the scroll) so the shopper can type.
      setTimeout(() => this.input?.focus({ preventScroll: true }), 160);
    } else {
      // Mobile overlay: lift to <body> so the fixed panel escapes the header's
      // sticky/stacking context.
      if (this.parentNode !== document.body) document.body.appendChild(this);
      this.style.visibility = '';
      this.lockScroll();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('active'));
      });
      setTimeout(() => this.input?.focus({ preventScroll: true }), 80);
    }
  }

  close() {
    clearTimeout(this._releaseTimer);

    if (this.desktop.matches) {
      // Pin the current rendered height (max-height may be 'none'), then animate
      // it down to 0 — an explicit px height collapses the whole band, background
      // and padding included, all the way with no stuck last bit.
      this.style.maxHeight = this.offsetHeight + 'px';
      void this.offsetHeight; // commit before transitioning
      this.classList.remove('active');
      requestAnimationFrame(() => {
        this.style.maxHeight = '0px';
      });
    } else {
      this.classList.remove('active');
    }

    this.unlockScroll();
    // Keep the dropdown content in place during the collapse; only reset the
    // predictive state AFTER the animation finishes.
    clearTimeout(this._collapseTimer);
    this._collapseTimer = setTimeout(() => {
      if (!this.classList.contains('active')) this.predictiveSearch?.close?.();
    }, 480);
    setTimeout(() => this.activeElement?.focus(), 50);
  }
}

customElements.define('search-drawer', SearchDrawer);

// Event delegation so this works even if the header rerenders (theme editor).
// The header search icon toggles the section open/closed.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.header__icon--search');
  if (!trigger) return;
  e.preventDefault();
  const drawer = document.querySelector('search-drawer');
  if (!drawer) return;
  if (drawer.classList.contains('active')) {
    drawer.close();
  } else {
    drawer.open(trigger);
  }
});
